/**
 * voice-v8 — Modul „Trainingsplan“: Typen, Grenzen, Eingabe-Prüfung, Konto-Tor und Notschalter. (20.10.2026, Paket P1)
 *
 * Konzept: docs/konzept-trainingsplan.md. Martin hat am 20.10. 22:05 per Knopf-Ja freigegeben:
 *   - Planmodus = fester Wochenplan, den Vespera täglich an Whoop anpasst (shared/training-regeln.ts, `tagesVorschlag`);
 *   - jede Änderung am Plan erst nach seinem Ja (Vorschlag → Ja, nie ungefragt; tools/training_plan.ts);
 *   - vorerst nur in Vespera, KEIN Kalender-Eintrag;
 *   - Körper-/Trainingsdaten im Mitlesestrom der Entwickler ausgeblendet (shared/koerper-mitleser.ts).
 *
 * ⚠️ Rein: keine Datenbank, kein Netz. Der Speicher (memory/training-store.ts) und die Werkzeuge prüfen mit DIESEN Regeln —
 *    eine Regel, ein Besitzer.
 * ⚠️ Kein ärztlicher Rat: der feste Satz `KEIN_ARZT_SATZ` steht in jeder Werkzeugbeschreibung und jedem Ergebnis.
 */

/** Die Arten eines Trainingsblocks (Konzept Abschnitt 2). */
export const TRAINING_ARTEN = ["aufwaermen", "ausdauer", "kraft", "balance", "mobilitaet", "dehnen", "faszien", "erholung"] as const;
export type TrainingArt = (typeof TRAINING_ARTEN)[number];

export const TRAINING_INTENSITAETEN = ["leicht", "mittel", "hart"] as const;
export type Intensitaet = (typeof TRAINING_INTENSITAETEN)[number];

/** Die Stufe des Tages: „voll“ = Plan wie geschrieben; „aus“ = bei Beschwerden kein Training. */
export const TRAINING_STUFEN = ["voll", "gedaempft", "leicht", "aus"] as const;
export type Stufe = (typeof TRAINING_STUFEN)[number];
/** Was Martin überstimmen darf („mach trotzdem voll“) — „aus“ setzt nur die Beschwerde-Regel. */
export type UeberstimmStufe = Exclude<Stufe, "aus">;

/** Die feste Regel „kein ärztlicher Rat“ — in Werkzeugbeschreibung und Ergebnis. */
export const KEIN_ARZT_SATZ =
  "Kein ärztlicher Rat. Bei Schmerz, Schwindel oder Brustdruck: Training aus und ärztlich abklären lassen.";
/** Der Grund des Tages, wenn Martin Beschwerden nennt. */
export const BESCHWERDE_SATZ = "Beschwerden genannt (Schmerz, Schwindel oder Brustdruck): heute kein Training — bitte ärztlich abklären lassen.";
/** Der Hinweis, wenn Whoop nichts Brauchbares liefert. */
export const OHNE_WHOOP_SATZ = "ohne Whoop-Werte";

/* ── Grenzen (Größenbegrenzung) ─────────────────────────────────────── */

export const TITEL_MAX = 60;
export const NOTIZ_MAX = 200;
export const PLAN_NAME_MAX = 60;

/* ── Das Knopf-Ja der Vorschlagskarte (21.10.2026) ──────────────────── */

/** Form einer Vorschlags-Kennung (tools/versand-bestaetigung.ts: 6 Zufallsbytes als Hex). */
export const VORSCHLAG_KENNUNG = /^[0-9a-f]{12}$/;

/**
 * Der feste Wortlaut, den der Ja-Knopf der Karte „Trainingsplan ändern“ als Martins Nachricht schickt — EINE Stelle für Karte
 * (frontend/lib/karten/training.ts) und Prüfung (tools/training_plan.ts). Nur feste Wörter und die Kennung, nie freier Text.
 */
export function trainingKnopfJa(vorschlagId: string): string {
  return `Ja, Trainingsplan so ändern (Vorschlag ${vorschlagId})`;
}
/** Der Nein-Knopf derselben Karte (verwirft nur, ist nie gesperrt). */
export function trainingKnopfNein(vorschlagId: string): string {
  return `Nein, Trainingsplan nicht ändern (Vorschlag ${vorschlagId})`;
}
export const DAUER_MAX_MIN = 300;
export const REIHENFOLGE_MAX = 99;
/** Höchstens so viele Blöcke an einem Wochentag, so viele Pläne und Ist-Einträge je Tag und Konto. */
export const EINHEITEN_JE_TAG_MAX = 10;
export const PLAENE_JE_KONTO_MAX = 10;
export const IST_JE_TAG_MAX = 20;
/** Ein Ist-Eintrag darf höchstens so weit zurückliegen (nie in der Zukunft). */
export const IST_RUECKWIRKEND_TAGE = 14;

/* ── Datensätze ─────────────────────────────────────────────────────── */

export interface TrainingPlan {
  id: number;
  name: string;
  /** YYYY-MM-DD (Wiener Tag). */
  gueltigAb: string;
  aktiv: boolean;
}

export interface TrainingEinheit {
  id: number;
  planId: number;
  /** 1 = Montag … 7 = Sonntag. */
  wochentag: number;
  reihenfolge: number;
  /** „HH:MM“ oder null. */
  uhrzeit: string | null;
  art: TrainingArt;
  titel: string;
  dauerMin: number;
  intensitaet: Intensitaet;
  notiz: string | null;
}

export interface TrainingIst {
  id: number;
  /** YYYY-MM-DD (Wiener Tag). */
  datum: string;
  einheitId: number | null;
  art: TrainingArt;
  dauerMin: number;
  gefuehl: number | null;
  quelle: "manuell" | "whoop";
}

/* ── Konto-Tor und Notschalter ──────────────────────────────────────── */

/** Im MVP nur Konto 1 (Modulfreigabe; andere Konten standardmäßig „nein“). */
export const TRAINING_KONTO = 1;

export function trainingFuerKonto(userId: number | null | undefined): boolean {
  return userId === TRAINING_KONTO;
}

/**
 * Notschalter `TRAININGSPLAN=0` (auch „aus“): nimmt Werkzeuge (und später die Karte) heraus, die Daten bleiben. Bei JEDER
 * Prüfung gelesen — Registrierung und jeder Aufruf. Rein.
 */
export function trainingAn(env: NodeJS.ProcessEnv = process.env): boolean {
  const w = env.TRAININGSPLAN?.trim().toLowerCase();
  return w !== "0" && w !== "aus";
}

/* ── Eingabe-Prüfung ────────────────────────────────────────────────── */

export type Pruefung<T> = { ok: true; wert: T } | { ok: false; grund: string };

const WOCHENTAG_NAMEN: Readonly<Record<string, number>> = {
  montag: 1, mo: 1, dienstag: 2, di: 2, mittwoch: 3, mi: 3, donnerstag: 4, do: 4, freitag: 5, fr: 5, samstag: 6, sa: 6, sonntag: 7, so: 7,
};
const WOCHENTAG_TITEL = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"] as const;

export function wochentagName(n: number): string {
  return WOCHENTAG_TITEL[n - 1] ?? "?";
}

const ganz = (v: unknown, min: number, max: number): number | null =>
  typeof v === "number" && Number.isInteger(v) && v >= min && v <= max ? v : null;

/** Ein Wochentag: 1–7 oder der deutsche Name („Montag“, „mo“). */
export function pruefeWochentag(v: unknown): number | null {
  if (typeof v === "string") {
    const n = WOCHENTAG_NAMEN[v.trim().toLowerCase()];
    if (n) return n;
    return /^[1-7]$/.test(v.trim()) ? Number(v.trim()) : null;
  }
  return ganz(v, 1, 7);
}

/** Dauer in Minuten, ganzzahlig 0–300. */
export function pruefeDauer(v: unknown): number | null {
  return ganz(v, 0, DAUER_MAX_MIN);
}

function ausListe<T extends string>(v: unknown, liste: readonly T[]): T | null {
  if (typeof v !== "string") return null;
  const t = v.trim().toLowerCase().replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue");
  return (liste as readonly string[]).includes(t) ? (t as T) : null;
}

export const pruefeArt = (v: unknown): TrainingArt | null => ausListe(v, TRAINING_ARTEN);
export const pruefeIntensitaet = (v: unknown): Intensitaet | null => ausListe(v, TRAINING_INTENSITAETEN);
export const pruefeUeberstimmen = (v: unknown): UeberstimmStufe | null => ausListe(v, ["voll", "gedaempft", "leicht"] as const);

/** Steuerzeichen und doppelte Leerräume weg, getrimmt. */
function sauber(s: string): string {
  // eslint-disable-next-line no-control-regex
  return s.replace(/[\u0000-\u001f\u007f\u200b-\u200f\u2028-\u202e\u2066-\u2069]/g, " ").replace(/\s+/g, " ").trim();
}

/** Ein kurzer Text (Titel, Name): nicht leer, höchstens `max` Zeichen. */
export function pruefeKurztext(v: unknown, max: number): string | null {
  if (typeof v !== "string") return null;
  const t = sauber(v);
  return t && [...t].length <= max ? t : null;
}

/** „HH:MM“ (24 h). */
export function pruefeUhrzeit(v: unknown): string | null {
  return typeof v === "string" && /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(v.trim()) ? v.trim() : null;
}

/** „YYYY-MM-DD“ — ein echtes Kalenderdatum. */
export function pruefeDatum(v: unknown): string | null {
  if (typeof v !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return null;
  const d = new Date(`${v}T00:00:00Z`);
  return Number.isFinite(d.getTime()) && d.toISOString().slice(0, 10) === v ? v : null;
}

/** Was eine neue Einheit braucht (Soll). */
export type EinheitEingabe = Omit<TrainingEinheit, "id" | "planId">;

/**
 * Eine Einheit prüfen. `teilweise`: nur die gesetzten Felder (Ändern); sonst sind wochentag, art, titel, dauer_min und
 * intensitaet Pflicht. Feldnamen wie im Werkzeug (snake_case). Unbekanntes ist ein Fehler mit Namen, nie ein stilles Weglassen.
 */
export function pruefeEinheit(roh: Record<string, unknown>, teilweise = false): Pruefung<Partial<EinheitEingabe>> {
  const aus: Partial<EinheitEingabe> = {};
  const da = (k: string): boolean => roh[k] !== undefined && roh[k] !== null;
  if (da("wochentag")) {
    const w = pruefeWochentag(roh.wochentag);
    if (w === null) return { ok: false, grund: "wochentag" };
    aus.wochentag = w;
  } else if (!teilweise) return { ok: false, grund: "wochentag" };
  if (da("art")) {
    const a = pruefeArt(roh.art);
    if (!a) return { ok: false, grund: "art" };
    aus.art = a;
  } else if (!teilweise) return { ok: false, grund: "art" };
  if (da("titel")) {
    const t = pruefeKurztext(roh.titel, TITEL_MAX);
    if (!t) return { ok: false, grund: "titel" };
    aus.titel = t;
  } else if (!teilweise) return { ok: false, grund: "titel" };
  if (da("dauer_min")) {
    const d = pruefeDauer(roh.dauer_min);
    if (d === null) return { ok: false, grund: "dauer_min" };
    aus.dauerMin = d;
  } else if (!teilweise) return { ok: false, grund: "dauer_min" };
  if (da("intensitaet")) {
    const i = pruefeIntensitaet(roh.intensitaet);
    if (!i) return { ok: false, grund: "intensitaet" };
    aus.intensitaet = i;
  } else if (!teilweise) return { ok: false, grund: "intensitaet" };
  if (da("uhrzeit")) {
    const u = pruefeUhrzeit(roh.uhrzeit);
    if (!u) return { ok: false, grund: "uhrzeit" };
    aus.uhrzeit = u;
  } else if (!teilweise) aus.uhrzeit = null;
  if (da("reihenfolge")) {
    const r = ganz(roh.reihenfolge, 0, REIHENFOLGE_MAX);
    if (r === null) return { ok: false, grund: "reihenfolge" };
    aus.reihenfolge = r;
  } else if (!teilweise) aus.reihenfolge = 0;
  if (da("notiz")) {
    if (typeof roh.notiz !== "string") return { ok: false, grund: "notiz" };
    const n = sauber(roh.notiz);
    if ([...n].length > NOTIZ_MAX) return { ok: false, grund: "notiz" };
    aus.notiz = n || null;
  } else if (!teilweise) aus.notiz = null;
  if (teilweise && Object.keys(aus).length === 0) return { ok: false, grund: "keine-aenderung" };
  return { ok: true, wert: aus };
}

/** Was ein Ist-Eintrag braucht. */
export interface IstEingabe {
  datum: string;
  einheitId: number | null;
  art: TrainingArt;
  dauerMin: number;
  gefuehl: number | null;
  quelle: "manuell" | "whoop";
  whoopWorkoutId: string | null;
}

/**
 * Einen Ist-Eintrag prüfen. `heute`: der Wiener Tag (YYYY-MM-DD); das Datum darf nicht in der Zukunft und nicht älter als
 * IST_RUECKWIRKEND_TAGE sein. Ohne Datum gilt heute.
 */
export function pruefeIst(roh: Record<string, unknown>, heute: string): Pruefung<IstEingabe> {
  const datum = roh.datum === undefined || roh.datum === null ? heute : pruefeDatum(roh.datum);
  if (!datum) return { ok: false, grund: "datum" };
  const abstand = (Date.parse(`${heute}T00:00:00Z`) - Date.parse(`${datum}T00:00:00Z`)) / 86_400_000;
  if (abstand < 0 || abstand > IST_RUECKWIRKEND_TAGE) return { ok: false, grund: "datum" };
  const art = pruefeArt(roh.art);
  if (!art) return { ok: false, grund: "art" };
  const dauer = pruefeDauer(roh.dauer_min);
  if (dauer === null) return { ok: false, grund: "dauer_min" };
  let einheitId: number | null = null;
  if (roh.einheit_id !== undefined && roh.einheit_id !== null) {
    einheitId = ganz(roh.einheit_id, 1, Number.MAX_SAFE_INTEGER);
    if (einheitId === null) return { ok: false, grund: "einheit_id" };
  }
  let gefuehl: number | null = null;
  if (roh.gefuehl !== undefined && roh.gefuehl !== null) {
    gefuehl = ganz(roh.gefuehl, 1, 5);
    if (gefuehl === null) return { ok: false, grund: "gefuehl" };
  }
  return { ok: true, wert: { datum, einheitId, art, dauerMin: dauer, gefuehl, quelle: "manuell", whoopWorkoutId: null } };
}

/* ── Kalender (Wiener Zeit) ─────────────────────────────────────────── */

const WIEN_TAG = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Vienna", year: "numeric", month: "2-digit", day: "2-digit" });

/** Der Wiener Kalendertag „YYYY-MM-DD“. */
export function wienerDatum(d: Date): string {
  return WIEN_TAG.format(d);
}

/** Wochentag eines Datums (1 = Montag … 7 = Sonntag). */
export function wochentagVon(datum: string): number {
  const t = new Date(`${datum}T00:00:00Z`).getUTCDay();
  return t === 0 ? 7 : t;
}

/** Das Datum `tage` Tage nach `datum` (negativ = davor). */
export function datumPlus(datum: string, tage: number): string {
  return new Date(Date.parse(`${datum}T00:00:00Z`) + tage * 86_400_000).toISOString().slice(0, 10);
}
